import React, { useMemo, useCallback, useRef } from 'react';
import Joi from '@hapi/joi';
import { Formik } from 'formik';
import { toast } from 'react-toastify';
import { useMutation, gql } from '@apollo/client';
import moment from 'moment';
import ReactGA from 'Shared/analytics';

import LeaveRequestView from './LeaveRequestView';
import { logger } from 'RootShared/utility/ErrorLogger';

const mutationAddLeaveRequest = gql`
  mutation LeaveRequestFormAddLeaveRequestMutation($input: LeaveRequestInput!) @api(name: root) {
    addLeaveRequest(leaveRequest: $input) {
      leaveRequest {
        dateFrom
        dateTo
        willWorkOn
        comment
        allocation {
          id
        }
        reason {
          id
        }
      }
    }
  }
`;

const LeaveRequestForm = ({
  data: { vacationReasons: reasonsData, allocation: allocationsData },
  handleDataReload,
}) => {
  const [addLeaveRequest] = useMutation(mutationAddLeaveRequest, {
    onCompleted(data) {
      onRequestVacationSuccess(data);
    },
    onError(error) {
      logger.error(error);
      onRequestVacationError(error);
    },
  });

  const initialValues = {
    dateFrom: '',
    dateTo: '',
    willWorkOn: '',
    comment: '',
    reason: reasonsData.length ? reasonsData[0].id : '',
    allocation: '',
  };

  const allocations = useMemo(
    () =>
      allocationsData.map(({ id, reason, validUntil, daysLeft }) => {
        const formatedText = `${reason.text} (${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} until ${moment(
          validUntil
        ).format('MMMM D \u00B7 YYYY')})`;

        return {
          id,
          daysLeft,
          text: reason.text,
          label: formatedText,
          value: formatedText,
          reasonId: reason.id,
          validUntil,
        };
      }),
    [allocationsData]
  );

  const reasons = useMemo(
    () =>
      reasonsData.map(({ id, text, requiresAllocation, requiresCompensatory }) => ({
        id,
        label: text,
        value: text,
        requiresAllocation,
        requiresCompensatory,
      })),
    [reasonsData]
  );

  const getValidationSchema = useCallback(
    (reason) => {
      return Joi.object().keys({
        reason: Joi.number().error(() => 'Reason must be selected'),
        allocation: Joi.any(),
        dateFrom: Joi.date().error(() => 'Dates must be selected'),
        dateTo: Joi.date().error(() => 'Dates must be selected'),
        comment: Joi.string().allow(''),
        willWorkOn: reasons.find(({ id }) => +id === +reason).requiresCompensatory
          ? Joi.string().error(() => 'Must be filled in for selected reason')
          : Joi.string().allow(''),
      });
    },
    [reasons]
  );

  const toastId = useRef(null);

  const showNotification = useCallback(
    () =>
      (toastId.current = toast('In progress, please wait...', {
        autoClose: true,
        closeButton: false,
        hideProgressBar: true,
      })),
    []
  );

  const onRequestVacationSuccess = useCallback(
    (data) => {
      if (!data.errors) {
        toast.update(toastId.current, {
          render: 'Your request has been submitted',
          type: toast.TYPE.SUCCESS,
          autoClose: 3000,
          closeButton: true,
          hideProgressBar: false,
        });
      } else {
        ReactGA.exception({
          description: data.error.message,
          fatal: true,
        });
        toast.update(toastId.current, {
          render: 'Error during saving request',
          type: toast.TYPE.ERROR,
          autoClose: 3000,
          closeButton: true,
          hideProgressBar: false,
        });
      }
      handleDataReload();
    },
    [toastId, handleDataReload]
  );

  const onRequestVacationError = useCallback(
    (error) => {
      toast.update(toastId.current, {
        render: error.message,
        type: toast.TYPE.ERROR,
        autoClose: 3000,
        closeButton: true,
        hideProgressBar: false,
      });
      return error;
    },
    [toastId]
  );

  const handleRequestSubmit = useCallback(
    (values, { setSubmitting, setValues }) => {
      const variables = (({ reason, allocation, dateTo, dateFrom, willWorkOn, comment }) => ({
        input: {
          comment,
          willWorkOn,
          dateFrom: moment(dateFrom).format('YYYY-MM-DD'),
          dateTo: moment(dateTo).format('YYYY-MM-DD'),
          reason: { id: reason ? +reason : null },
          allocation: { id: allocation ? +allocation : null },
        },
      }))(values);

      ReactGA.event({
        category: 'Leave request',
        action: 'Add vacation submit',
        label: values.reason ? values.reason : null,
      });

      showNotification();
      addLeaveRequest({ variables: variables });

      setSubmitting(false);
      setValues(initialValues);
    },
    [addLeaveRequest, initialValues, showNotification]
  );

  const handleRequestValidation = useCallback(
    async (values) => {
      const result = await Joi.validate(
        {
          ...values,
          allocation: values.allocation ? values.allocation : null,
        },
        getValidationSchema(values.reason),
        {
          abortEarly: false,
        }
      );

      return result.error;
    },
    [getValidationSchema]
  );

  const renderFunction = useCallback(
    (props) => <LeaveRequestView formikProps={props} reasonsData={reasons} allocationsData={allocations} />,
    [reasons, allocations]
  );

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleRequestSubmit}
      validate={handleRequestValidation}
      validateOnChange={false}
      validateOnBlur={true}
      render={renderFunction}
    />
  );
};

export default LeaveRequestForm;
